<template>
    <Teleport to="#main-layout">
        <div class="spotlight-overlay" @click.self="close" @keydown.esc="close">
            <div class="spotlight-container">
                <div class="spotlight-search-row">
                    <i class="fa-solid fa-magnifying-glass spotlight-search-icon"></i>
                    <input ref="inputRef" class="spotlight-input" :placeholder="$t('Search a Room...')"
                        v-model="queryText" @input="onInput" autocomplete="off" />
                    <button class="spotlight-close-btn" :title="$t('Close')" @click="close">
                        <i class="fa-solid fa-xmark"></i>
                    </button>
                </div>
                <div v-if="showResults" class="spotlight-results">
                    <RoomsList :rooms="rooms" :more="more" :loading="loading" @load-more="loadMore" @select="selectRoom"
                        :query="q"></RoomsList>
                </div>
                <div v-else-if="queryText && !loading" class="spotlight-empty">
                    {{ $t("No rooms found") }}
                </div>
                <div v-else-if="loading" class="spotlight-empty">
                    <i class="fa-solid fa-spinner fa-spin"></i>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script lang="ts">
import { defineComponent, nextTick } from "vue";
import { Request } from "@asanrom/request-browser";
import { Timeouts } from "@/utils/timeout";
import { getUniqueStringId } from "@/utils/unique-id";
import { saveIntoLocalStorage } from "@/utils/local-storage";
import { RoomController } from "@/control/room";
import { ApiRoom } from "@/api/api-group-room";
import RoomsList from "@/components/utils/RoomsList.vue";

export default defineComponent({
    name: "SpotlightSearchModal",
    components: { RoomsList },
    emits: ["close"],
    setup() {
        return {
            loadRequestId: getUniqueStringId(),
        };
    },
    data() {
        return {
            queryText: "",
            q: "",
            qPending: "",
            loading: false,
            more: true,
            rooms: [] as any[],
            skip: null as number | null,
            showResults: false,
            next: false,
        };
    },
    methods: {
        close() {
            this.$emit("close");
        },

        onInput() {
            const query = this.queryText;
            if (!query) {
                this.q = "";
                this.qPending = "";
                this.loading = false;
                this.more = true;
                this.rooms = [];
                this.skip = null;
                this.showResults = false;
                return;
            }
            this.qPending = query;
            this.checkPendingQuery();
        },

        checkPendingQuery() {
            if (this.qPending !== this.q) {
                this.q = this.qPending;
                this.onQueryChanged();
            }
        },

        onQueryChanged() {
            Request.Abort(this.loadRequestId);
            this.loading = false;
            this.more = true;
            this.rooms = [];
            this.skip = null;
            this.showResults = false;
            this.loadRooms();
        },

        loadMore() {
            this.loadRooms();
        },

        loadRooms() {
            if (this.loading || !this.more) {
                return;
            }

            const query = this.q || "*";
            this.loading = true;

            const url = this.next
                ? ApiRoom.GetRoomsSearch({ q: query, skip: this.skip })
                : ApiRoom.GetRoomsSearch({ q: query });

            Request.Pending(this.loadRequestId, url)
                .onSuccess((response) => {
                    this.loading = false;
                    this.showResults = true;
                    for (let i = 0; i < response.rooms.length; i++) {
                        this.rooms.push(response.rooms[i]);
                    }
                    if (response.next) {
                        this.next = true;
                        this.more = true;
                        this.skip = response.skip;
                    } else {
                        this.next = false;
                        this.more = false;
                    }
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        temporalError: () => {
                            Timeouts.Set(this.loadRequestId, 1500, this.loadRooms.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    Timeouts.Set(this.loadRequestId, 1500, this.loadRooms.bind(this));
                });
        },

        selectRoom(rid: string) {
            if (!rid) return;
            this.close();
            RoomController.joinRoom(rid);
            saveIntoLocalStorage("father-id", "myrooms");
            this.$router.push({ name: "room", params: { roomId: rid } });
        },

        onKeyDown(e: KeyboardEvent) {
            if (e.key === "Escape") {
                this.close();
            }
        },
    },
    mounted() {
        document.addEventListener("keydown", this.onKeyDown);
        nextTick(() => {
            (this.$refs.inputRef as HTMLInputElement)?.focus();
        });
    },
    beforeUnmount() {
        document.removeEventListener("keydown", this.onKeyDown);
        Request.Abort(this.loadRequestId);
        Timeouts.Abort(this.loadRequestId);
    },
});
</script>

<style scoped>
.spotlight-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 10vh;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    animation: spotlight-fade-in 0.15s ease;
}

@keyframes spotlight-fade-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.spotlight-container {
    width: min(660px, 92vw);
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
    animation: spotlight-slide-in 0.18s cubic-bezier(0.22, 1, 0.36, 1);
    background: var(--bar-bg-color);
    border: solid 1px var(--grey-maya);
}

.light-theme .spotlight-container {
    background: rgba(255, 255, 255, 0.97);
}

.dark-theme .spotlight-container {
    background: rgba(28, 28, 30, 0.97);
}

@keyframes spotlight-slide-in {
    from {
        transform: translateY(-18px) scale(0.97);
        opacity: 0;
    }

    to {
        transform: translateY(0) scale(1);
        opacity: 1;
    }
}

.spotlight-search-row {
    display: flex;
    align-items: center;
    padding: 0 16px;
    border-bottom: solid 1px var(--grey-maya);
    height: 58px;
    gap: 12px;
}

.spotlight-search-icon {
    font-size: 20px;
    color: var(--red-maya);
    flex-shrink: 0;
}

.spotlight-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 1.15rem;
    color: var(--text-color);
    font-weight: 500;
    caret-color: var(--red-maya);
    border-radius: 8px;
}

.light-theme .spotlight-input::placeholder {
    color: black;
    font-weight: 400;
}

.dark-theme .spotlight-input::placeholder {
    color: white;
    font-weight: 400;
}




.spotlight-close-btn {
    background: transparent;
    border: none;
    padding: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--text-color);
    opacity: 0.6;
    font-size: 16px;
    transition: opacity 0.15s, background 0.15s;
    flex-shrink: 0;
}

.spotlight-close-btn:hover {
    opacity: 1;
    background: var(--hover-color);
}

.spotlight-results {
    max-height: 55vh;
    overflow-y: auto;
    overflow-x: hidden;
}

.spotlight-empty {
    padding: 32px;
    text-align: center;
    color: var(--grey-maya);
    font-size: 1rem;
}
</style>
